import { Component, OnDestroy, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { MessageService } from 'primeng/api';
import { ContractMaintenanceService } from '../../services/contract-maintenance.service';
import { ContractMaintenance } from '../../models/contract-maintenance.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export type ContractTypeLabel = 'MSA' | 'SOW' | 'Proposal';

export interface ContractSummaryRow {
  customerName: string;
  projectName: string;
  type: ContractTypeLabel;
  expiryDate: string | null; // ISO date
  contractId: string;
  /** 0-11 for selected year; -1 if expiry not in selected year */
  expiryMonth: number;
}

export interface CalendarDay {
  day: number;
  date: Date;
  isExpiryDay: boolean;
}

@Component({
  selector: 'app-contracts-summary-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Tooltip,
    Tag
  ],
  templateUrl: './contracts-summary-view.component.html',
  styleUrl: './contracts-summary-view.component.scss'
})
export class ContractsSummaryViewComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  loading = true;
  selectedYear = new Date().getFullYear();
  rows: ContractSummaryRow[] = [];
  filteredRows: ContractSummaryRow[] = [];
  contracts: ContractMaintenance[] = [];
  private searchText = '';
  selectedTypeFilter: ContractTypeLabel | null = null;

  get totalCount(): number { return this.filteredRows.length; }
  get expiringInYearCount(): number { return this.filteredRows.filter(r => r.expiryMonth !== -1).length; }
  get msaCount(): number { return this.filteredRows.filter(r => r.type === 'MSA').length; }
  get sowCount(): number { return this.filteredRows.filter(r => r.type === 'SOW').length; }
  get proposalCount(): number { return this.filteredRows.filter(r => r.type === 'Proposal').length; }

  showExpiryDialog = false;
  /** When dialog is open: which row was clicked (so we show its expiry date) */
  dialogRow: ContractSummaryRow | null = null;
  /** When opening from a month click, we can show calendar for that month */
  dialogMonth = 0;
  dialogYear = new Date().getFullYear();
  calendarDays: CalendarDay[] = [];

  months = [
    { label: 'Jan', value: 0, fullName: 'January' },
    { label: 'Feb', value: 1, fullName: 'February' },
    { label: 'Mar', value: 2, fullName: 'March' },
    { label: 'Apr', value: 3, fullName: 'April' },
    { label: 'May', value: 4, fullName: 'May' },
    { label: 'Jun', value: 5, fullName: 'June' },
    { label: 'Jul', value: 6, fullName: 'July' },
    { label: 'Aug', value: 7, fullName: 'August' },
    { label: 'Sep', value: 8, fullName: 'September' },
    { label: 'Oct', value: 9, fullName: 'October' },
    { label: 'Nov', value: 10, fullName: 'November' },
    { label: 'Dec', value: 11, fullName: 'December' }
  ];

  constructor(
    private contractService: ContractMaintenanceService,
    private messageService: MessageService,
    private pageHeaderService: PageHeaderSearchActionService,
    private yearMonthFilterService: YearMonthFilterService
  ) {
    this.selectedYear = this.yearMonthFilterService.currentYear;
  }

  ngOnInit(): void {
    // Sync year from the shared header year dropdown
    this.yearMonthFilterService.year
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(year => {
        if (year !== this.selectedYear) {
          this.selectedYear = year;
          this.buildRows();
        }
      });

    // Header integration: search + Type filter select
    this.pageHeaderService.setPlaceholder('Search by customer, project, type...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.pageHeaderService.setExportDisabled(false);
    this.pageHeaderService.setFilterSelect(
      [
        { label: 'MSA', value: 'MSA' },
        { label: 'SOW', value: 'SOW' },
        { label: 'Proposal', value: 'Proposal' }
      ],
      'All Types'
    );
    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.searchText = (text ?? '').trim().toLowerCase();
        this.applySearch();
      });
    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());
    this.pageHeaderService.filterSelectChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.selectedTypeFilter = value as ContractTypeLabel | null;
        this.applySearch();
      });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData(): void {
    this.loading = true;
    this.contractService.getAll().subscribe({
      next: (list) => {
        this.contracts = list;
        this.buildRows();
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load contracts.' });
        this.loading = false;
      }
    });
  }

  private buildRows(): void {
    const result: ContractSummaryRow[] = [];
    for (const c of this.contracts) {
      if (c.msaExpiryDate) {
        const expiry = new Date(c.msaExpiryDate);
        result.push({
          customerName: c.clientName,
          projectName: c.projectName,
          type: 'MSA',
          expiryDate: c.msaExpiryDate,
          contractId: c.id,
          expiryMonth: expiry.getFullYear() === this.selectedYear ? expiry.getMonth() : -1
        });
      }
      if (c.sowExpiryDate) {
        const expiry = new Date(c.sowExpiryDate);
        result.push({
          customerName: c.clientName,
          projectName: c.projectName,
          type: 'SOW',
          expiryDate: c.sowExpiryDate,
          contractId: c.id,
          expiryMonth: expiry.getFullYear() === this.selectedYear ? expiry.getMonth() : -1
        });
      }
      if (c.proposalExpiryDate) {
        const expiry = new Date(c.proposalExpiryDate);
        result.push({
          customerName: c.clientName,
          projectName: c.projectName,
          type: 'Proposal',
          expiryDate: c.proposalExpiryDate,
          contractId: c.id,
          expiryMonth: expiry.getFullYear() === this.selectedYear ? expiry.getMonth() : -1
        });
      }
    }
    this.rows = result;
    this.applySearch();
  }

  private applySearch(): void {
    let filtered = this.rows;
    const term = this.searchText;
    if (term) {
      filtered = filtered.filter(r =>
        (r.customerName ?? '').toLowerCase().includes(term) ||
        (r.projectName ?? '').toLowerCase().includes(term) ||
        (r.type ?? '').toLowerCase().includes(term)
      );
    }
    if (this.selectedTypeFilter) {
      filtered = filtered.filter(r => r.type === this.selectedTypeFilter);
    }
    this.filteredRows = filtered;
  }

  isExpiryMonth(row: ContractSummaryRow, monthValue: number): boolean {
    return row.expiryMonth === monthValue;
  }

  getMonthCellTooltip(row: ContractSummaryRow, monthValue: number): string {
    if (row.expiryMonth !== monthValue) return '';
    return row.expiryDate
      ? `Expires: ${this.formatExpiryDate(row.expiryDate)}. Click to see details.`
      : '';
  }

  openExpiryDialog(row: ContractSummaryRow, monthValue: number): void {
    this.dialogRow = row;
    this.dialogMonth = monthValue;
    this.dialogYear = this.selectedYear;
    this.buildCalendarDays();
    this.showExpiryDialog = true;
  }

  getCalendarMonthName(): string {
    return this.months.find(m => m.value === this.dialogMonth)?.fullName ?? '';
  }

  buildCalendarDays(): void {
    const year = this.dialogYear;
    const month = this.dialogMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const expiryDay = this.getExpiryDayInMonth();

    this.calendarDays = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({
        day: 0,
        date: new Date(year, month, 0),
        isExpiryDay: false
      });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      this.calendarDays.push({
        day: d,
        date: new Date(year, month, d),
        isExpiryDay: d === expiryDay
      });
    }
  }

  navigateCalendarMonth(direction: number): void {
    this.dialogMonth += direction;
    if (this.dialogMonth < 0) {
      this.dialogMonth = 11;
      this.dialogYear--;
    } else if (this.dialogMonth > 11) {
      this.dialogMonth = 0;
      this.dialogYear++;
    }
    this.buildCalendarDays();
  }

  isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  closeExpiryDialog(): void {
    this.showExpiryDialog = false;
    this.dialogRow = null;
  }

  getDialogExpiryDateFormatted(): string {
    if (!this.dialogRow?.expiryDate) return '—';
    return this.formatExpiryDate(this.dialogRow.expiryDate);
  }

  /** Calendar: day of month that is the expiry (1-31), or 0 if not in this month */
  getExpiryDayInMonth(): number {
    if (!this.dialogRow?.expiryDate) return 0;
    const d = new Date(this.dialogRow.expiryDate);
    if (d.getFullYear() !== this.dialogYear || d.getMonth() !== this.dialogMonth) return 0;
    return d.getDate();
  }

  private formatExpiryDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private exportToExcel(): void {
    if (this.filteredRows.length === 0) return;
    const data = this.filteredRows.map((r, i) => ({
      '#': i + 1,
      'Customer': r.customerName || '',
      'Project': r.projectName || '',
      'Type': r.type || '',
      'Expiry Date': r.expiryDate ? this.formatExpiryDate(r.expiryDate) : '-',
      'Expiry Month': r.expiryMonth >= 0 ? this.months.find(m => m.value === r.expiryMonth)?.fullName ?? '-' : '-'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 4 }, { wch: 28 }, { wch: 32 }, { wch: 12 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contracts Summary');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `contracts_summary_${this.selectedYear}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  getTypeSeverity(type: ContractTypeLabel): 'success' | 'info' | 'warn' | 'secondary' {
    switch (type) {
      case 'MSA': return 'info';
      case 'SOW': return 'success';
      case 'Proposal': return 'warn';
      default: return 'secondary';
    }
  }
}
